import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { 
  Users, 
  MapPin, 
  Phone, 
  Mail, 
  Clock, 
  ArrowRight
} from "lucide-react";
import logoUrl from "@/assets/natcon-logo.png";
import mapmavenLogo from "@/assets/mapmaven-logo.jpg";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: "About Us", href: "/about" },
    { name: "Membership", href: "/membership" },
    { name: "Loan Calculator", href: "/calculator" },
    { name: "Member Portal", href: "https://selfservice.natconsacco.or.ke/" },
    { name: "Contact Us", href: "/contact" },
    { name: "FAQs", href: "/faqs" }
  ];

  const products = [
    { name: "Member Deposits", href: "/products#savings" },
    { name: "Share Capital", href: "/products#savings" },
    { name: "Normal (Development) Loan", href: "/products#loans" },
    { name: "Emergency Loan", href: "/products#loans" },
    { name: "Education Loan", href: "/products#loans" },
    { name: "Device Loan", href: "/products#loans" }
  ];

  const resources = [
    { name: "News & Media", href: "/news-media" },
    { name: "Careers", href: "/careers" },
    { name: "Tenders", href: "/tenders" }
  ];

  return (
    <footer className="bg-primary text-primary-foreground">
      <div className="container mx-auto px-4 py-12 lg:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-8">
          {/* Company Info */}
          <div className="space-y-6">
            <div className="flex items-center space-x-3">
              <img 
                src={logoUrl}
                alt="Natcon Staff Sacco Logo"
                className="h-12 w-auto"
                loading="lazy"
                width="48"
                height="48"
              />
              <div>
                <h3 className="text-xl font-bold">Natcon Staff Sacco</h3>
                <p className="text-sm text-primary-foreground/80">Your Partner in Prosperity</p>
              </div>
            </div>
            
            <p className="text-primary-foreground/80 leading-relaxed">
              For over 10 yrs, Natcon Staff Sacco has been empowering members to achieve their financial goals 
              through innovative products and exceptional service.
            </p>

            <div className="space-y-3">
              <div className="flex items-start gap-3 text-sm">
                <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div>
                  <div>KCB Towers, Kenya Road</div>
                  <div>Housed in National Construction Authority NCA</div>
                  <div>P.O. Box 21046-00100</div>
                </div>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <Phone className="h-4 w-4 flex-shrink-0" />
                <span>+254111485951</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <Mail className="h-4 w-4 flex-shrink-0" />
                <span className="break-all">natconsacco@nca.go.ke</span>
              </div>
              <div className="flex items-start gap-3 text-sm">
                <Clock className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>Mon-Fri: 8:00 AM - 5:00 PM</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-lg mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <a 
                    href={link.href}
                    className="text-primary-foreground/80 hover:text-accent transition-colors flex items-center gap-2 group"
                  >
                    <ArrowRight className="h-3 w-3 group-hover:translate-x-1 transition-transform" />
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Products & Services */}
          <div>
            <h4 className="font-semibold text-lg mb-6">Products & Services</h4>
            <ul className="space-y-3">
              {products.map((product) => (
                <li key={product.name}>
                  <a 
                    href={product.href}
                    className="text-primary-foreground/80 hover:text-accent transition-colors flex items-center gap-2 group"
                  >
                    <ArrowRight className="h-3 w-3 group-hover:translate-x-1 transition-transform" />
                    {product.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="font-semibold text-lg mb-6">Resources</h4>
            <ul className="space-y-3">
              {resources.map((resource) => (
                <li key={resource.name}>
                  <a 
                    href={resource.href}
                    className="text-primary-foreground/80 hover:text-accent transition-colors flex items-center gap-2 group"
                  >
                    <ArrowRight className="h-3 w-3 group-hover:translate-x-1 transition-transform" />
                    {resource.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Newsletter Signup */}
        <div className="mt-16 mb-12">
          <div className="bg-primary-foreground/5 rounded-xl p-8 text-center">
            <h3 className="text-2xl font-bold mb-2">Stay Connected</h3>
            <p className="text-primary-foreground/80 mb-6">
              Subscribe to our newsletter for the latest updates, financial tips, and exclusive offers.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input 
                type="email" 
                placeholder="Enter your email address"
                className="flex-1 px-4 py-3 rounded-lg text-foreground bg-background border border-border focus:ring-2 focus:ring-accent focus:border-transparent text-sm"
              />
              <Button className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold whitespace-nowrap">
                Subscribe
              </Button>
            </div>
          </div>
        </div>

        <Separator className="bg-primary-foreground/20" />

        {/* Bottom Section */}
        <div className="mt-8 text-center space-y-4">
          <p className="text-primary-foreground/80 text-sm">
            © {currentYear} Natcon Staff Sacco. All rights reserved.
          </p>
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            <a href="/privacy-policy" className="text-primary-foreground/80 hover:text-accent transition-colors min-h-[44px] flex items-center">
              Privacy Policy
            </a>
            <a href="/terms-of-service" className="text-primary-foreground/80 hover:text-accent transition-colors min-h-[44px] flex items-center">
              Terms of Service
            </a>
            <a href="/sitemap" className="text-primary-foreground/80 hover:text-accent transition-colors min-h-[44px] flex items-center">
              Sitemap
            </a>
          </div>
          
          {/* Developer Credit */}
          <div className="pt-2">
            <a 
              href="https://www.mapmavengmbexperts.com" 
              target="_blank" 
              rel="noopener noreferrer nofollow"
              title="Website developed by MapMaven GMB Experts"
              className="inline-flex items-center gap-2 text-primary-foreground/60 hover:text-primary-foreground/80 transition-colors text-xs"
            >
              <span>Website by</span>
              <img 
                src={mapmavenLogo}
                alt="MapMaven GMB Experts - Website Developer Logo"
                className="h-5 w-auto"
                loading="lazy"
              />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;